import { defineEventSchemas, defineTraitsSchema } from '@glomopay/analytics';
import { z } from 'zod';

import { AnalyticsEventName } from '../constants/analytics-event.constants';
import {
  API_FAILURE_KINDS,
  API_RESULT_STATUSES,
  AppErrorCode,
  CHECKOUT_FLOWS,
  CHECKOUT_OUTCOME_EVENTS,
  CheckoutErrorCode,
  CloseTrigger,
  CONTROL_VALUES,
  ControlName,
  CtaName,
  ENTRY_ACTIONS,
  ENTRY_ERROR_CODES,
  ERROR_CODE_PATTERN,
  HOLDING_VIEWS,
  PageName,
  SCHEME_NO_PATTERN,
  SessionErrorCode,
} from '../constants/analytics-property.constants';
import { buildEventSchema } from '../utils/build-event-schema';
import { pickApiPaths } from '../utils/pick-api-paths';
import { pickHttpMethods } from '../utils/pick-http-methods';

const errorCodeSchema = z.string().regex(ERROR_CODE_PATTERN);

const countSchema = z.number().int().nonnegative();

const checkoutFlowSchema = z.enum(CHECKOUT_FLOWS);

const holdingViewSchema = z.enum(HOLDING_VIEWS);

export const apiBackendCallPropertiesSchema = z.object({
  entry_action: z.enum(ENTRY_ACTIONS).optional(),
  is_re_resolve: z.boolean().optional(),
  is_resend: z.boolean().optional(),
  attempt: z.number().int().positive().optional(),
  flow: checkoutFlowSchema.optional(),
});

export const ANALYTICS_EVENT_SCHEMAS = defineEventSchemas({
  [AnalyticsEventName.INVESTOR_APP_LOADED]: buildEventSchema({ has_handoff_payload: z.boolean(), has_live_session: z.boolean() }),
  [AnalyticsEventName.INVESTOR_APP_FAILED]: buildEventSchema({ error_code: z.nativeEnum(AppErrorCode) }),
  [AnalyticsEventName.INVESTOR_PAGE_VIEWED]: buildEventSchema({
    page_name: z.nativeEnum(PageName),
    scheme_count: countSchema.optional(),
    folio_count: countSchema.optional(),
    has_holdings: z.boolean().optional(),
    holding_view: holdingViewSchema.optional(),
    transaction_count: countSchema.optional(),
    has_transactions: z.boolean().optional(),
    flow: checkoutFlowSchema.optional(),
  }),
  [AnalyticsEventName.INVESTOR_CTA_CLICKED]: buildEventSchema({
    cta_name: z.nativeEnum(CtaName),
    holding_view: holdingViewSchema.optional(),
    scheme_no: z.string().regex(SCHEME_NO_PATTERN).optional(),
    flow: checkoutFlowSchema.optional(),
  }),
  [AnalyticsEventName.INVESTOR_CONTROL_CHANGED]: buildEventSchema({ control_name: z.nativeEnum(ControlName), new_value: z.enum(CONTROL_VALUES) }),
  [AnalyticsEventName.INVESTOR_API_BACKEND]: buildEventSchema({
    ...apiBackendCallPropertiesSchema.shape,
    api_path: z.enum(pickApiPaths()),
    http_method: z.enum(pickHttpMethods()),
    status: z.enum(API_RESULT_STATUSES),
    http_status: z.number().int().optional(),
    error_code: errorCodeSchema.optional(),
    failure_kind: z.enum(API_FAILURE_KINDS).optional(),
  }),
  [AnalyticsEventName.INVESTOR_ENTRY_FAILED]: buildEventSchema({ error_code: z.enum(ENTRY_ERROR_CODES) }),
  [AnalyticsEventName.INVESTOR_SESSION_FAILED]: buildEventSchema({ error_code: z.nativeEnum(SessionErrorCode) }),
  [AnalyticsEventName.INVESTOR_CHECKOUT_SUCCEEDED]: buildEventSchema({ flow: checkoutFlowSchema, outcome_event: z.enum(CHECKOUT_OUTCOME_EVENTS) }),
  [AnalyticsEventName.INVESTOR_CHECKOUT_FAILED]: buildEventSchema({ flow: checkoutFlowSchema, error_code: z.nativeEnum(CheckoutErrorCode) }),
  [AnalyticsEventName.INVESTOR_CHECKOUT_CLOSED]: buildEventSchema({ flow: checkoutFlowSchema, trigger: z.nativeEnum(CloseTrigger) }),
});

export const ANALYTICS_TRAITS_SCHEMA = defineTraitsSchema(z.object({ id: z.string() }));
